/**
 * MarketingProjectView.jsx — Feb 2026 · Phase 84.23
 *
 * Tabbed marketing layout rendered inside ProjectDetail when the project has
 * `marketing_hub === true`. Tab order (post 84.23):
 *
 *   Overview · Top Posts · Plan · Hook Library · AI News · Analytics
 *
 * "Plan" is the unified Composer + Calendar with drag-and-drop scheduling
 * across IST / EST / PST / MST + platform-native previews for
 * LinkedIn / Instagram / YouTube / Google Business.
 *
 * The old "Demo data" banner is gone — we now show a small connection strip
 * that reports LIVE followers from Meta Business (FB + IG) via
 * /api/marketing/live-stats.
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import OverviewSection from "@/pages/marketing/OverviewSection";
import TopPostsSection from "@/pages/marketing/TopPostsSection";
import PlanBoard from "@/pages/marketing/PlanBoard";
import NewsSection from "@/pages/marketing/NewsSection";
import Analytics from "@/pages/Analytics";
import DailyTasksWidget from "@/components/DailyTasksWidget";
const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "posts",     label: "Top posts" },
  { id: "plan",      label: "Plan" },          // Phase 84.23 — was Composer + Calendar
  { id: "tasks",     label: "Tasks" },         // Phase 84.34 — mirrored posts + team tasks
  { id: "news",      label: "AI news" },
  { id: "analytics", label: "Analytics" },
];

export default function MarketingProjectView({ projectId }) {
  const [tab, setTab] = useState("overview");
  const [drafts, setDrafts] = useState([]);
  const [liveStats, setLiveStats] = useState({ channels: [] });
  const [members, setMembers] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [d, s, u] = await Promise.all([
        api.get("/marketing/drafts"),
        api.get("/marketing/live-stats"),
        api.get("/users?kind=internal").catch(() => ({ data: [] })),
      ]);
      setDrafts(d.data || []);
      setLiveStats(s.data || { channels: [] });
      setMembers((u.data || []).filter((x) => (x.status || "active") === "active"));
    } catch (e) { logger.debug("marketing project view load failed", e?.message || e); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const connectedCount = (liveStats.channels || []).filter((c) => c.connected).length;
  const pendingCount = (liveStats.channels || []).filter((c) => !c.connected).length;

  // Phase 84.35 — clicking "YouTube · not connected" fires the shared Google
  // OAuth flow. The updated scope list (`youtube.readonly`) means one consent
  // now unlocks Calendar + Sheets + Search Console + YouTube in a single hop.
  const connectYouTube = useCallback(async () => {
    try {
      const { data } = await api.post("/integrations/google/connect");
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error("Could not start Google OAuth");
      }
    } catch (e) {
      toast.error("Could not start Google OAuth");
      logger.error("youtube connect failed", e);
    }
  }, []);

  // Phase 84.37 — LinkedIn + GMB scaffolds. Endpoint returns
  // {configured: false, setup: [steps…]} until creds land / App Review
  // completes. We surface those steps in a rich toast so the user knows
  // exactly what's needed without leaving the page.
  const connectPlatform = useCallback((platform, label) => async () => {
    try {
      const { data } = await api.post(`/integrations/${platform}/connect`);
      if (data?.configured && data?.auth_url) {
        window.location.href = data.auth_url;
        return;
      }
      const steps = (data?.setup || []).join("\n");
      toast.message(`${label} · setup required`, {
        description: steps || "Awaiting App Review — no user action needed yet.",
        duration: 12000,
      });
    } catch (e) {
      toast.error(`Could not check ${label} status`);
      logger.error(`${platform} connect failed`, e);
    }
  }, []);

  return (
    <div className="space-y-4" data-testid="marketing-project-view">
      {/* Live connection strip — replaces the old "Demo data" amber banner. */}
      <div className="card-flat p-3 flex items-start gap-2 border-l-4 border-l-[#F54800]" data-testid="marketing-live-strip">
        <div className="w-6 h-6 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
          <CheckCircle2 size={13} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-900">
            {connectedCount} platform{connectedCount === 1 ? "" : "s"} connected
            <span className="ml-2 text-slate-400 font-normal">· {pendingCount} awaiting App Review</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
            {(liveStats.channels || []).map((c) => {
              // Phase 84.35/37 — disconnected channels become clickable
              // "Connect" pills. YouTube hits the shared Google OAuth;
              // LinkedIn/Pinterest/GMB hit their own status endpoint which
              // returns a setup checklist when creds/App Review are pending.
              const connectable = !c.connected && ["youtube", "linkedin", "pinterest", "gmb"].includes(c.id);
              const onClick = !connectable
                ? undefined
                : c.id === "youtube"
                  ? connectYouTube
                  : connectPlatform(c.id, c.label);
              const Tag = connectable ? "button" : "span";
              return (
                <Tag
                  key={c.id}
                  onClick={onClick}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm ${c.connected ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"} ${connectable ? "hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300 cursor-pointer transition-colors" : ""}`}
                  data-testid={`marketing-live-${c.id}`}
                >
                  {c.connected
                    ? <><CheckCircle2 size={10} /> {c.label}{c.followers != null && ` · ${c.followers.toLocaleString()}`}{c.handle && <span className="text-emerald-600"> · {c.handle}</span>}</>
                    : connectable
                      ? <><ExternalLink size={10} /> Connect {c.label}</>
                      : <><AlertTriangle size={10} /> {c.label} · not connected</>}
                </Tag>
              );
            })}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            YouTube is now LIVE via your Google account. LinkedIn / Pinterest / GMB require each platform&apos;s App Review (2-6 weeks). Composer captures your schedule and publish time — see the Plan tab.
          </div>
        </div>
      </div>

      {/* Single tab row */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="marketing-project-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap transition-colors ${tab === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            data-testid={`marketing-project-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div data-testid={`marketing-project-panel-${tab}`}>
        {tab === "overview"  && <OverviewSection liveStats={liveStats} />}
        {tab === "posts"     && <TopPostsSection />}
        {tab === "plan"      && <PlanBoard drafts={drafts} onChanged={loadAll} members={members} />}
        {tab === "tasks"     && (
          <div data-testid="marketing-project-tasks">
            <div className="text-[11px] text-slate-500 mb-2">
              Team tasks for this marketing project. Cards with a
              <span className="inline-flex items-center gap-0.5 text-pink-600 mx-1"><svg width="10" height="10" fill="currentColor" viewBox="0 0 20 20"><rect x="2" y="4" width="16" height="12" rx="2" strokeWidth="0" /></svg>camera</span>
              are automatically mirrored from a scheduled post on the Plan tab — editing either side syncs both.
            </div>
            <DailyTasksWidget scopeProjectId={projectId} defaultAssignee="all" />
          </div>
        )}
        {tab === "news"      && <NewsSection canEdit />}
        {tab === "analytics" && <Analytics />}
      </div>
    </div>
  );
}
